import { CREATE, LOAD, RECONCILE, RESET, UPDATE } from 'model/palIfpEnrolled';
import {
  LOAD_OPERATIONS,
  LOAD_OPERATION_DETAIL,
} from 'model/palIfpEnrolledOps';
import {
  createEnrollment,
  getEnrollmentState,
  reconcileEnrollment,
  resetEnrollment,
  updateDesiredState,
  getOperations,
  getOperation,
} from 'sdk/palIfpEnrolled';
import { takeLatest } from 'redux-saga/effects';
import { callAction, ORM } from '../util';

const formatErrorMessage = (errorObj, callData) => {
  const status =
    errorObj?.status ?? errorObj?.statusCode ?? errorObj?.response?.status;
  if (status === 409) {
    return 'Conflict: an operation is already in progress. Please wait and try again.';
  }
  if (status === 412) {
    return 'Version mismatch: the enrollment was updated by another user. Please reload.';
  }

  // BFF errors put data directly on the object; raw Axios errors nest under response.data
  const responseErrors =
    errorObj?.data?.errors ??
    errorObj?.errors ??
    errorObj?.response?.data?.errors;
  if (Array.isArray(responseErrors) && responseErrors.length > 0) {
    if (typeof callData?.onApiErrors === 'function') {
      callData.onApiErrors(responseErrors);
    }
    return responseErrors.map((e) => e.message).join(' | ');
  }

  const message =
    errorObj?.data?.message ?? errorObj?.message ?? errorObj?.error?.message;
  return message || 'An error occurred';
};

const reloadState = callAction({
  api: getEnrollmentState,
  dataName: ORM,
  type: LOAD,
});

const reloadOperationsWorker = callAction({
  api: getOperations,
  dataName: ORM,
  type: LOAD_OPERATIONS,
});

function* reloadOperations(action) {
  const result = yield reloadOperationsWorker(action);
  if (result && typeof action?.data?.onNextToken === 'function') {
    const nextToken = result?.response?.nextToken ?? null;
    action.data.onNextToken(nextToken);
  }
}

function* createEnrollmentAndReload(action) {
  const worker = callAction({
    api: createEnrollment,
    dataName: ORM,
    type: CREATE,
    formatSuccessMessage: () => 'IFP enrollment provisioning started',
    formatErrorMessage,
  });

  const result = yield worker(action);
  if (result) {
    const fspId = action?.data;
    if (fspId) {
      yield reloadState({ data: fspId });
      yield reloadOperations({ data: { fspId } });
    }
  }
}

function* updateAndReload(action) {
  const worker = callAction({
    api: updateDesiredState,
    dataName: ORM,
    type: UPDATE,
    formatSuccessMessage: () => 'IFP enrollment desired state update queued',
    formatErrorMessage,
  });

  const result = yield worker(action);
  if (result) {
    const fspId = action?.data?.fspId;
    if (fspId) {
      yield reloadState({ data: fspId });
      yield reloadOperations({ data: { fspId } });
    }
  }
}

function* reconcileAndReload(action) {
  const worker = callAction({
    api: reconcileEnrollment,
    dataName: ORM,
    type: RECONCILE,
    formatSuccessMessage: () => 'IFP enrollment reconciliation started',
    formatErrorMessage,
  });

  const result = yield worker(action);
  if (result) {
    const fspId = action?.data;
    if (fspId) {
      yield reloadState({ data: fspId });
      yield reloadOperations({ data: { fspId } });
    }
  }
}

export default function* saga() {
  yield takeLatest(CREATE.ACTION, createEnrollmentAndReload);

  yield takeLatest(LOAD.ACTION, reloadState);

  yield takeLatest(
    RESET.ACTION,
    callAction({
      api: resetEnrollment,
      dataName: ORM,
      type: RESET,
    })
  );

  yield takeLatest(UPDATE.ACTION, updateAndReload);

  yield takeLatest(RECONCILE.ACTION, reconcileAndReload);

  yield takeLatest(LOAD_OPERATIONS.ACTION, reloadOperations);

  yield takeLatest(
    LOAD_OPERATION_DETAIL.ACTION,
    callAction({
      api: getOperation,
      dataName: ORM,
      type: LOAD_OPERATION_DETAIL,
    })
  );
}
